import { Injectable } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import ngLocalePt from '@angular/common/locales/pt';
import ngLocalePtExtra from '@angular/common/locales/extra/pt';
import ngLocaleEs from '@angular/common/locales/es-MX';
import ngLocaleEsExtra from '@angular/common/locales/extra/es-MX';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { lowerCase } from 'lodash';
import * as moment from 'moment-timezone';

import { StorageService } from './storage.service';
import { Logger } from './logger.service';
import { UrlParamsService } from './url-params.service';
import { DateFormatEnum, Locale, LocaleInput } from '@app/models';
import { SelectOption } from '@app/shared/select/select.component';
import * as enUS from '/src/translations/en-US.json';
import * as ptBR from '/src/translations/pt-BR.json';
import * as esMX from '/src/translations/es-MX.json';

const countryList = require('country-list');
const log = new Logger('I18nService');
const languageKey = 'language';

/**
 * Pass-through function to mark a string for translation extraction.
 * Running `npm translations:extract` will include the given string by using this.
 * @param s The string to extract for translation.
 * @return The same string.
 */
export function extract(s: string) {
  return s;
}

@Injectable({
  providedIn: 'root'
})
export class I18nService {
  defaultLanguage!: string;
  supportedLanguages!: string[];

  private langChangeSubscription!: Subscription;
  private _locale: Locale;

  constructor(
    private translateService: TranslateService,
    private storageService: StorageService,
    private urlParamsService: UrlParamsService
  ) {
    registerLocaleData(ngLocalePt, 'pt', ngLocalePtExtra);
    registerLocaleData(ngLocaleEs, 'es-MX', ngLocaleEsExtra);

    // Embed languages to avoid extra HTTP requests
    translateService.setTranslation('en-US', enUS.default || enUS);
    translateService.setTranslation('pt-BR', ptBR.default || ptBR);
    translateService.setTranslation('es-MX', esMX.default || esMX);
  }

  /**
   * Initializes i18n for the application.
   * Loads language from local storage if present, or sets default language.
   * @param defaultLanguage The default language to use.
   * @param supportedLanguages The list of supported languages.
   */
  init(defaultLanguage: string, supportedLanguages: string[]) {
    this.defaultLanguage = defaultLanguage;
    this.supportedLanguages = supportedLanguages;
    this.language = '';

    // Warning: this subscription will always be alive for the app's lifetime
    this.langChangeSubscription = this.translateService.onLangChange.subscribe({
      next: (event: LangChangeEvent) => {
        this.storageService.set(languageKey, event.lang);
      }
    });
  }

  /**
   * Cleans up language change subscription.
   */
  destroy() {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }

  get locale(): Locale {
    return this._locale;
  }

  set locale(value: Locale) {
    this._locale = value || {};

    // Prioritizes values set in URL parameters
    const urlLocale = this.localeFromUrlParams();
    this._locale.language = urlLocale.language || this._locale.language;
    this._locale.timezone = urlLocale.timezone || this._locale.timezone;
    this._locale.date_format = urlLocale.date_format || this._locale.date_format;

    if (this._locale.language) {
      this.language = this._locale.language;
    }
  }

  /**
   * Sets the current language.
   * Note: The current language is saved to the local storage.
   * If no parameter is specified, the language is loaded from local storage (if present).
   * @param language The IETF language code to set.
   */
  set language(language: string) {
    let lang = language || this.storageService.get(languageKey) || this.translateService.getBrowserCultureLang();
    let isSupportedLanguage = this.supportedLanguages.includes(lang);

    // If no exact match is found, search without the region
    if (lang && !isSupportedLanguage) {
      lang = lang.split('-')[0];
      lang = this.supportedLanguages.find(supportedLanguage => supportedLanguage.startsWith(lang)) || '';
      isSupportedLanguage = Boolean(lang);
    }

    // Fallback if language is not supported
    if (!isSupportedLanguage) {
      lang = this.defaultLanguage;
    }

    if (this.language !== lang) {
      log.debug(`Language set to ${lang}`);
      this.translateService.use(lang);
    }
  }

  /**
   * Gets the current language.
   * @return The current language code.
   */
  get language(): string {
    return this.translateService.currentLang;
  }

  get supportedLanguagesAsSelectOptions(): SelectOption[] {
    return this.supportedLanguages.map(language => {
      return {
        key: language,
        value: language.toUpperCase(),
        height: 46,
        fontSize: 16
      };
    });
  }

  get dateFormatOptions(): SelectOption[] {
    return Object.keys(DateFormatEnum).map(key => {
      const format = DateFormatEnum[key as keyof typeof DateFormatEnum];
      return {
        key: format,
        value: this.dateFormatAsMask(format),
        height: 46,
        fontSize: 16
      };
    });
  }

  get timezonesWithGMT(): SelectOption[] {
    const timezones = moment.tz.names();
    return timezones.map(tz => {
      const gmt = moment.tz(tz).format('Z'); // Gets the GMT of the timezone
      return {
        key: tz,
        value: `${tz} (GMT ${gmt})`,
        height: 46,
        fontSize: 16
      };
    });
  }

  get timezone(): string {
    return moment.tz.guess(false);
  }

  get countriesWithCodes(): SelectOption[] {
    const countryNames = countryList.getNames();
    const countryList$ = countryNames.map((name: string) => {
      const code = countryList.getCode(name);
      return {
        key: code,
        value: this.translateService.instant(`countryCodes.${lowerCase(code)}`),
        height: 46,
        fontSize: 16,
        icon: `fi fi-${lowerCase(code)}`
      };
    });

    const prioritizedCountries = ['BR', 'US', 'PT'];
    const prioritized = countryList$.filter((country: SelectOption) => prioritizedCountries.includes(country.key));
    const others = countryList$.filter((country: SelectOption) => !prioritizedCountries.includes(country.key));

    return [...prioritized, ...others];
  }

  dateFormatAsMask(value?: DateFormatEnum, raw?: boolean): string {
    const format = value || this._locale?.date_format;
    if (!format) return;

    if (raw) {
      return format.replace(/_/g, '/');
    }

    return format.replace(/DD/g, 'dd').replace(/MM/g, 'MM').replace(/YYYY/g, 'yyyy').replace(/_/g, '/');
  }

  getTimezoneOffset(tz?: Locale['timezone']): string {
    const timezone = tz || this._locale?.timezone;
    if (timezone) {
      const currentTime = moment.tz(timezone).format('Z');
      return `UTC${currentTime}`;
    }
  }

  navigatorCountry() {
    return window.navigator.language.split('-')[1];
  }

  prepareLocaleInput(): LocaleInput {
    return {
      country: this.navigatorCountry(),
      language: this.language,
      timezone: this.timezone,
      date_format: this.language === 'en-US' ? DateFormatEnum.MmDdYyyy : DateFormatEnum.DdMmYyyy
    } as LocaleInput;
  }

  getSelectDisplayValue(options: SelectOption[], value: string): string {
    return options.find(option => option?.key === value)?.value || value || '';
  }

  private localeFromUrlParams(): Locale {
    const locale: Locale = {
      language: this.urlParamsService.params.language,
      timezone: this.urlParamsService.params.timezone
    };
    if (locale.language) {
      locale.date_format = locale.language === 'en-US' ? DateFormatEnum.MmDdYyyy : DateFormatEnum.DdMmYyyy;
    }

    return locale;
  }
}
